import React, { FunctionComponent, useState, useEffect, useRef } from 'react';
import { Presentation } from './presentation';
import { relatedRecruitment } from '../../../types/recruitmentShow';
import { isNotFulfilledProfiledForRecruitment } from '../../../services/recruitments';
import { CareerEmploymentType, ProfileStep } from '../../../types/personShow';
import { useFlash } from '../../../hooks/useFlash';
import { updateApi } from '../../../services/personProfiles';
import { getCsrfToken } from '../../../libs/csrfToken';
import { keyMap } from './constants';
import { ApiValue, ModalProfileState, ResumeKey } from './types';
import { isEndAfterStart, isValidYearMonth } from '../../../libs/dateUtils';
import { sendResumes } from '../../../services/resumes';
import { sendJobResumes } from '../../../services/jobResumes';

type Props = {
  isConnectCompleteModalOpen: boolean;
  recruitmentId?: number;
  personId: number;
  isLoggedIn: boolean;
  isCompany: boolean;
  hasRequiredProfiles: boolean;
  needProfileUpdate: boolean;
  requiredProfileNames: string[];
  relatedRecruitments: relatedRecruitment[];
  companyName: string;
  handleClickModalBg: () => void;
  profileImmediateInputFlag: boolean;
  enforceFlag: boolean;
  birthdayList: number[];
  startYearList: number[];
  endYearList: number[];
  careerEmploymentTypes: CareerEmploymentType[];
};

export const Component: FunctionComponent<Props> = ({
  isConnectCompleteModalOpen,
  recruitmentId,
  personId: initialPersonId,
  isLoggedIn,
  isCompany,
  hasRequiredProfiles,
  needProfileUpdate: initialNeedProfileUpdate,
  requiredProfileNames,
  relatedRecruitments,
  companyName,
  handleClickModalBg,
  profileImmediateInputFlag,
  enforceFlag,
  birthdayList,
  startYearList,
  endYearList,
  careerEmploymentTypes,
}) => {
  const [personId, setPersonId] = useState<number | null>(initialPersonId);
  const [needProfileUpdate, setNeedProfileUpdate] = useState<boolean>(initialNeedProfileUpdate);

  useEffect(() => {
    const request = async () => {
      if (!recruitmentId) return;
      try {
        const response = await isNotFulfilledProfiledForRecruitment(recruitmentId);
        setPersonId(response.personId);
        setNeedProfileUpdate(response.needProfileUpdate);
        setMissingProfiles(response.missingProfiles);
        // 各種STEPバー項目初期化
        setCurrentIndex(0);
        setCompletedKeys([]);
      } catch (error) {
        console.log(error);
      }
    }

    if (isLoggedIn && profileImmediateInputFlag) {
      request();
    }
  }, [isLoggedIn, profileImmediateInputFlag]);

  // モーダル内のプロフィール入力関連
  const flashState = useFlash();
  const [isLoading, setIsLoading] = useState(false);
  const [missingProfiles, setMissingProfiles] = useState<ProfileStep[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentStep = missingProfiles[currentIndex];
  const [completedKeys, setCompletedKeys] = useState<string[]>([]);
  const [modalState, setModalState] = useState<ModalProfileState>({
    deleted: false,  // 未使用
    educationalHistories: [],
    careerHistories: [],
    skills: [],  // 未使用
    interests: [],  // 未使用
    favoriteBrands: [],
    preferredLocations: [],  // 未使用
    introduction: '',
    activity: '',
    slogan: '',
    motivation: '',  // 未使用
    birthday: '',  // 未使用
    resume: {
      resume: '',
    },
    job_resume: {
      job_resume: ''
    },
  });

  const handleChangeModalState = <K extends keyof ModalProfileState>(
    key: K,
    value: ModalProfileState[K]
  ) => {
    setModalState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const getApiValue = (
    key: keyof ModalProfileState
  ): ApiValue | null => {
    switch (key) {
      case 'favoriteBrands':
        return modalState.favoriteBrands.join(',');
      case 'educationalHistories':
        return modalState.educationalHistories;
      case 'careerHistories':
        return modalState.careerHistories;
      case 'introduction':
        return modalState.introduction;
      case 'activity':
        return modalState.activity;
      case 'slogan':
        return modalState.slogan;
      default:
        return null;
    }
  };

  const getResumeFileValue = (
    type: ResumeKey,
    state: ModalProfileState
  ): File | '' | { name: { url: string } } => {
    if (type === 'resume') {
      return state.resume.resume;
    }
    return state.job_resume.job_resume;
  };

  const validateStep = (
    key: keyof ModalProfileState,
    state: ModalProfileState
  ): string | null => {
    switch (key) {
      case 'educationalHistories': {
        const targetHistory = state.educationalHistories[0];
        // 未入力は許可（disabled制御へ移行）
        if (!targetHistory) {
          return null;
        }
        // 開始年月
        if (
          targetHistory.startDate &&
          targetHistory.startDate !== '/' &&
          !targetHistory.startDate.includes('NaN') &&
          !isValidYearMonth(targetHistory.startDate)
        ) {
          return '在籍期間（開始年/月）が正しく入力されていません。';
        }
        // 終了年月
        if (
          targetHistory.endDate &&
          targetHistory.endDate !== '現在' &&
          !targetHistory.endDate.includes('NaN') &&
          !isValidYearMonth(targetHistory.endDate)
        ) {
          return '在籍期間（終了年/月）が正しく入力されていません。';
        }
        // 開始 > 終了
        if (
          targetHistory.startDate &&
          targetHistory.endDate &&
          targetHistory.endDate !== '現在' &&
          isValidYearMonth(targetHistory.startDate) &&
          isValidYearMonth(targetHistory.endDate) &&
          !isEndAfterStart(targetHistory.startDate, targetHistory.endDate)
        ) {
          return '在籍期間の終了は開始より後の日付を選択してください。';
        }
        return null;
      }
      case 'careerHistories': {
        const targetHistory = state.careerHistories[0];
        // 未入力は許可（disabled制御へ移行）
        if (!targetHistory) {
          return null;
        }
        // 開始年月
        if (
          targetHistory.startDate &&
          targetHistory.startDate !== '/' &&
          !targetHistory.startDate.includes('NaN') &&
          !isValidYearMonth(targetHistory.startDate)
        ) {
          return '在籍期間（開始年/月）が正しく入力されていません。';
        }
        // 終了年月（現職でない場合）
        if (
          !targetHistory.currentWorking &&
          targetHistory.endDate &&
          targetHistory.endDate !== '/' &&
          !targetHistory.endDate.includes('NaN') &&
          !isValidYearMonth(targetHistory.endDate)
        ) {
          return '在籍期間（終了年/月）が正しく入力されていません。';
        }
        // 開始 > 終了
        if (
          !targetHistory.currentWorking &&
          targetHistory.startDate &&
          targetHistory.endDate &&
          isValidYearMonth(targetHistory.startDate) &&
          isValidYearMonth(targetHistory.endDate) &&
          !isEndAfterStart(targetHistory.startDate, targetHistory.endDate)
        ) {
          return '在籍期間の終了は開始より後の日付を選択してください。';
        }
        return null;
      }
      default:
        return null;
    }
  };

  const isStepSubmittable = (
    key: keyof ModalProfileState,
    state: ModalProfileState
  ): boolean => {
    switch (key) {
      case 'introduction':
        return state.introduction.trim() !== '';
      case 'activity':
        return state.activity.trim() !== '';
      case 'slogan':
        return state.slogan.trim() !== '';
      case 'favoriteBrands':
        return state.favoriteBrands.length > 0;
      case 'educationalHistories': {
        const targetHistory = state.educationalHistories[0];
        if (!targetHistory) {
          return false;
        }
        return !!(
          targetHistory.name &&
          targetHistory.name.trim() !== '' &&
          targetHistory.startDate &&
          targetHistory.startDate !== '/' &&
          !targetHistory.startDate.includes('NaN') &&
          targetHistory.endDate &&
          targetHistory.endDate !== '/' &&
          !targetHistory.endDate.includes('NaN')
        );
      }
      case 'careerHistories': {
        const targetHistory = state.careerHistories[0];
        if (!targetHistory) {
          return false;
        }
        const hasRequiredFields =
          !!targetHistory.name &&
          targetHistory.name.trim() !== '' &&
          !!targetHistory.startDate &&
          targetHistory.startDate !== '/' &&
          !targetHistory.startDate.includes('NaN') &&
          targetHistory.position.length > 0;
        if (targetHistory.currentWorking) {
          return hasRequiredFields;
        }
        return (
          hasRequiredFields &&
          !!targetHistory.endDate &&
          targetHistory.endDate !== '/' &&
          !targetHistory.endDate.includes('NaN')
        );
      }
      case 'resume': {
        const value = state.resume.resume;
        return value !== '';
      }
      case 'job_resume': {
        const value = state.job_resume.job_resume;
        return value !== '';
      }
      default:
        return true;
    }
  };

  const isCurrentStepSubmittable =
    currentStep &&
    keyMap[currentStep.key]
      ? isStepSubmittable(keyMap[currentStep.key], modalState)
      : true;

  const handleNext = async () => {
    if (!currentStep || !personId) return;

    const modalKey = keyMap[currentStep.key];
    if (!modalKey) {
      handleSkip();
      return;
    }

    if (modalKey === 'resume' || modalKey === 'job_resume') {
      const succeeded = await handleSendResumes(modalKey);

      if (!succeeded) {
        return;
      }

      setCompletedKeys((prev) =>
        prev.includes(currentStep.key) ? prev : [...prev, currentStep.key]
      );

      goNextStep();

      return;
    }

    setIsLoading(true);

    const errorMessage = validateStep(modalKey, modalState);
    if (errorMessage) {
      flashState.handleFlash(true, 'danger', errorMessage);
      setIsLoading(false);
      return;
    }

    const value = getApiValue(modalKey);
    if (value === null) {
      setIsLoading(false);
      handleSkip();
      return;
    }

    try {
      await updateApi(
        personId,
        {
          target: modalKey,
          value: value,
        },
        getCsrfToken()
      );
      flashState.handleFlash(true, 'success', `${currentStep.label}を更新しました`);
    } catch (error) {
      console.error(error);
      flashState.handleFlash(true, 'danger', `${currentStep.label}の更新に失敗しました。`);
      return;
    } finally {
      setIsLoading(false);
    }
    // 保存した場合のみdoneに追加
    setCompletedKeys((prev) =>
      prev.includes(currentStep.key) ? prev : [...prev, currentStep.key]
    );
    goNextStep();
  };

  const goNextStep = () => {
    if (currentIndex < missingProfiles.length - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
    } else {
      handleClickModalBg();
    }
  };

  const handleSkip = () => {
    goNextStep();
  };

  const handleChangeStep = (index: number) => {
    setCurrentIndex(index);
  };

  const handleSendResumes = async (type: ResumeKey): Promise<boolean> => {
    if (!currentStep || !personId) return false;
    setIsLoading(true);
    // 保存されているが、ただモーダルを開いて保存ボタンを押してもエラーにならないためのエラーハンドリング
    // ResumeStates オブジェクト
    const value = getResumeFileValue(type, modalState);

    // ファイル未選択（変更なし）は成功扱い
    if (!(value instanceof File) && value !== '') {
      setIsLoading(false);
      return true;
    }

    const file = value instanceof File ? value : '';
    try {
      const response =
        type === 'resume'
          ? await sendResumes(personId, file, getCsrfToken())
          : await sendJobResumes(personId, file, getCsrfToken());
      setModalState((prev) => ({
        ...prev,
        [type]: response.states,
      }));
      return true;
    } catch (error) {
      console.error(error);
      flashState.handleFlash(true, 'danger', `${currentStep.label}の更新に失敗しました。`);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Presentation
      isConnectCompleteModalOpen={isConnectCompleteModalOpen}
      personId={personId}
      isLoggedIn={isLoggedIn}
      isCompany={isCompany}
      hasRequiredProfiles={hasRequiredProfiles}
      needProfileUpdate={needProfileUpdate}
      requiredProfileNames={requiredProfileNames}
      relatedRecruitments={relatedRecruitments}
      companyName={companyName}
      handleClickModalBg={handleClickModalBg}
      // モーダル内のプロフィール入力関連
      profileImmediateInputFlag={profileImmediateInputFlag}
      enforceFlag={enforceFlag}
      birthdayList={birthdayList}
      startYearList={startYearList}
      endYearList={endYearList}
      careerEmploymentTypes={careerEmploymentTypes}
      flashState={flashState}
      isLoading={isLoading}
      missingProfiles={missingProfiles}
      currentIndex={currentIndex}
      currentStep={currentStep}
      completedKeys={completedKeys}
      modalState={modalState}
      handleChangeModalState={handleChangeModalState}
      isCurrentStepSubmittable={isCurrentStepSubmittable}
      handleNext={handleNext}
      handleSkip={handleSkip}
      handleChangeStep={handleChangeStep}
    />
  );
};
